import {
  Banner,
  Box,
  Breadcrumb,
  Button,
  Divider,
  Grid,
  GridColumn,
  Icon,
  Link,
  Logo,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Margin, usePDF } from "react-to-pdf";

import { ErrorModal } from "@/components/ErrorModal/ErrorModal";
import PaymentDetails from "@/components/InvoicePay/PaymentDetails";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { SchedulePayModal } from "@/components/Payments/AutoPayModal/SchedulePayModal";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { useInvoiceDetail } from "@/hooks/useInvoiceDetail";
import { usePaymentDetails } from "@/hooks/usePaymentDetails";
import {
  Invoice,
  InvoiceItem,
  InvoiceStatus,
  PaymentInfo
} from "@/types/Invoice";
import { SchedulePayTypes } from "@/types/PaymentMethod";
import {
  formatCurrency,
  formatDate,
  formatInvoiceAddress,
  formatTimestampDate,
  getPaymentStatus
} from "@/utils/InvoicePay";

import { formatInvoiceStatus } from "../utils/InvoicePay";

const InvoiceDetailPage = () => {
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const {
    fetchInvoice,
    isLoading: isInvoiceLoading,
    error: invoiceDetailError
  } = useInvoiceDetail();
  const { paymentInfo, isLoading: isPaymentLoading } = usePaymentDetails({
    invoiceNo
  });

  const [invoice, setInvoice] = React.useState<Invoice | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(!!invoiceDetailError);
  const history = useHistory();

  const pages = [
    { pageId: "your-account", title: "Your Account" },
    {
      pageId: "your-account/invoices-and-payments",
      title: "Invoices and payments"
    },
    {
      pageId: `your-account/invoices-and-payments/invoices/${invoiceNo}`,
      title: `Invoice #${invoiceNo}`
    }
  ];

  React.useEffect(() => {
    const fetchInvoiceDetail = async () => {
      const invoiceDetail = await fetchInvoice(invoiceNo);
      if (invoiceDetail) {
        setInvoice(invoiceDetail);
      }
    };
    fetchInvoiceDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNo]);

  const handleClickPage = React.useCallback(
    (pageId: string) => {
      history.push(`/${pageId}`);
    },
    [history]
  );

  React.useEffect(() => {
    if (!isInvoiceLoading && invoiceDetailError) {
      setShowErrorModal(true);
    }
  }, [invoiceDetailError, isInvoiceLoading]);

  return (
    <ThemeProvider>
      <ErrorModal
        isVisible={showErrorModal}
        setVisible={setShowErrorModal}
        title={invoiceDetailError}
        onDismiss={() => history.push(`/your-account/invoices-and-payments`)}
      />
      <Box
        sx={{
          backgroundColor: "surface-neutral-secondary",
          pt: "spacing-9",
          pb: "spacing-12"
        }}
      >
        <Grid>
          <GridColumn sx={{ pb: "spacing-9" }}>
            <Breadcrumb pages={pages} onClickPage={handleClickPage} />
          </GridColumn>
        </Grid>

        {isInvoiceLoading || isPaymentLoading ? (
          <LoadingSpinner size="xl" />
        ) : !invoice ? (
          <Text sx={{ pt: "spacing-6" }}>Invoice not found.</Text>
        ) : (
          <InvoiceDetailContent invoice={invoice} paymentInfo={paymentInfo} />
        )}
      </Box>
    </ThemeProvider>
  );
};

const InvoiceDetailContent: React.FC<{
  invoice: Invoice;
  paymentInfo: PaymentInfo | null;
}> = ({ invoice, paymentInfo }) => {
  const history = useHistory();
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);
  const [scheduleModalType, setScheduleModalType] =
    React.useState<SchedulePayTypes>(SchedulePayTypes.IndividualInvoices);

  const { toPDF, targetRef } = usePDF({
    filename: `Moderna_Invoice_${invoice.invoiceNo}.pdf`,
    page: {
      margin: Margin.SMALL,
      format: "A4"
    }
  });

  const autoPayAndSchedulePayEnabled = useFeatureFlag(
    "IS_AUTO_PAY_SCHEDULE_PAY_ENABLED"
  );

  if (!invoice) return null;

  const showPaymentDetails =
    invoice.status === InvoiceStatus.Paid ||
    invoice.status === InvoiceStatus.Pending;

  const showPayNow = !showPaymentDetails;
  const showSchedulePayment =
    autoPayAndSchedulePayEnabled &&
    !showPaymentDetails &&
    !invoice.payment?.paymentScheduled;
  const showManagePayment =
    (autoPayAndSchedulePayEnabled && invoice.payment?.paymentScheduled) ||
    false;

  const promptPayDiscount = invoice.discounts.find(
    (discount) => discount.discountType === "PROMPT_PAY"
  );

  return (
    <Grid>
      {autoPayAndSchedulePayEnabled && (
        <SchedulePayModal
          modalType={scheduleModalType}
          isVisible={showScheduleModal}
          setVisible={setShowScheduleModal}
          invoiceId={invoice.invoiceNo}
        />
      )}
      <GridColumn>
        {getPaymentStatus(formatInvoiceStatus(invoice.status))}
      </GridColumn>
      <GridColumn>
        <InvoiceHeader
          showManagePayment={showManagePayment}
          paymentScheduledDate={invoice.payment?.paymentScheduledDate}
          onClickDownload={toPDF}
        />
      </GridColumn>
      <GridColumn>
        <Box
          ref={targetRef}
          sx={{
            backgroundColor: "white",
            padding: "spacing-8",
            display: "flex",
            flexDirection: "column",
            borderRadius: "radius-2"
          }}
        >
          <Logo logo="moderna-logo" sx={{ height: 16, width: 48 }} />
          {/* Order numbers */}
          <Grid>
            <GridColumn>
              {invoice.poNumber && (
                <Text weight="bold">
                  P.O. number:
                  <Text weight="regular" sx={{ pl: "spacing-2" }}>
                    {invoice.poNumber}
                  </Text>
                </Text>
              )}
              <Text weight="bold">
                Order number:
                {invoice.orderNumber ? (
                  <Text
                    sx={{ pl: "spacing-2", fontWeight: "font-weight-regular" }}
                  >
                    {invoice.orderNumber}
                  </Text>
                ) : (
                  <Text weight="regular" sx={{ pl: "spacing-2" }}>
                    Order number not available.
                  </Text>
                )}
              </Text>
              <Text weight="bold">
                Invoice number:
                {invoice.invoiceNo ? (
                  <Text
                    sx={{ pl: "spacing-2", fontWeight: "font-weight-regular" }}
                  >
                    {invoice.invoiceNo}
                  </Text>
                ) : (
                  <Text weight="regular" sx={{ pl: "spacing-2" }}>
                    Invoice number not available.
                  </Text>
                )}
              </Text>
              <Text weight="bold">
                Invoice date:
                <Text weight="regular" sx={{ pl: "spacing-2" }}>
                  {formatTimestampDate(invoice.invoiceDate)}
                </Text>
              </Text>
              <Text weight="bold">
                Payment terms:
                <Text weight="regular" sx={{ pl: "spacing-2" }}>
                  {invoice.paymentTermsDescription}
                </Text>
              </Text>
            </GridColumn>
          </Grid>

          {/* Shipping and billing addresses */}
          <Grid sx={{ px: "spacing-4", py: "spacing-10" }}>
            <GridColumn size={2} fillGapEnd={true}>
              <Text weight="bold">Ship to</Text>
              <Text>{invoice.shipTo.name}</Text>
              <Text>{formatInvoiceAddress(invoice.shipTo)}</Text>
              {/* 
              todo: MD-350 to add medical license
              <Text weight="bold">
                Medical license:{" "}
                <Text weight="regular">{invoice.shipTo.medicalLicense}</Text>
              </Text> */}
              <Text weight="bold">
                Account number:{" "}
                <Text weight="regular">{invoice.shipTo.id}</Text>
              </Text>
            </GridColumn>
            <GridColumn size={2} fillGapEnd={true}>
              <Text weight="bold">Billed to</Text>
              <Text>{invoice.billTo.name}</Text>
              <Text>{formatInvoiceAddress(invoice.billTo)}</Text>
              <Text weight="bold">
                Account number:{" "}
                <Text weight="regular">{invoice.billTo.id}</Text>
              </Text>
            </GridColumn>
          </Grid>

          {invoice?.orderLineItems?.length > 0 ? (
            <ItemizedInvoiceTable items={invoice.orderLineItems} />
          ) : invoice?.notes?.length > 0 ? (
            <NoteCard notes={invoice.notes} />
          ) : (
            <Text>
              This order was placed outside of Moderna Direct, so detailed
              information is not available.
            </Text>
          )}

          {/*  Subtotals */}
          <Grid sx={{ pt: "spacing-9", pb: "spacing-6" }}>
            <GridColumn push={[0, 4, 8]}>
              <Grid>
                <GridColumn size={3} sx={{ alignItems: "end" }}>
                  <Text variant="body-1-medium">Subtotal:</Text>
                  {/* 
                  todo: MD-348 to add online discount
                  <Text variant="body-1-regular">Online discount (2%):</Text> 
                  */}
                </GridColumn>
                <GridColumn
                  size={1}
                  fillGapStart={true}
                  sx={{ alignItems: "end" }}
                >
                  <Text variant="body-1-medium">
                    {formatCurrency(invoice.subtotal)}
                  </Text>
                  {/* 
                  todo: MD-348 to add online discount
                  <Text variant="body-1-regular">
                    {formatCurrency(-(onlineDiscount?.discountAmount ?? 0))}
                  </Text> */}
                </GridColumn>
              </Grid>
            </GridColumn>

            <GridColumn size={2} push={[2, 6, 10]}>
              <Box sx={{ display: "inline-block", py: "spacing-3" }}>
                <Divider color="color-system-gray-100" />
              </Box>
            </GridColumn>

            <GridColumn push={[0, 4, 8]}>
              <Grid>
                <GridColumn size={3} sx={{ alignItems: "end" }}>
                  {/* 
                  todo: MD-348 to add online discount
                  <Text variant="body-1-medium">New subtotal:</Text> 
                  */}
                  <Text variant="body-1-regular">Sales tax:</Text>
                </GridColumn>
                <GridColumn
                  size={1}
                  fillGapStart={true}
                  sx={{ alignItems: "end" }}
                >
                  {/* 
                  todo: MD-348 to add online discount
                  <Text variant="body-1-medium">
                    $
                    {formatCurrency(
                      invoice.subtotal - (onlineDiscount?.discountAmount ?? 0)
                    )}
                  </Text> */}
                  <Text variant="body-1-regular">
                    {formatCurrency(invoice.taxes ?? 0)}
                  </Text>
                </GridColumn>
              </Grid>
            </GridColumn>
          </Grid>

          {/* Total amount due */}
          <Grid>
            <GridColumn size={4} push={[0, 4, 8]}>
              <Box sx={{ display: "inline-block", pb: "spacing-6" }}>
                <Divider color="color-current-brand-600" />
              </Box>
            </GridColumn>
            <GridColumn push={[0, 2, 4]}>
              <Grid>
                <GridColumn
                  size={[2, 4, 6]}
                  fillGapEnd={true}
                  sx={{ alignItems: "end", color: "color-current-brand-800" }}
                >
                  <Text variant="h5">Total amount due</Text>
                  {promptPayDiscount && (
                    <Text variant="body-1-medium">
                      If paid by
                      <Text sx={{ mx: "spacing-2" }}>
                        {formatTimestampDate(
                          promptPayDiscount?.discountExpiration
                        )}
                      </Text>
                      (2% prompt pay discount)
                    </Text>
                  )}
                  {invoice.status === "overdue" && (
                    <Text variant="body-1-medium">
                      Invoice due on
                      <Text sx={{ mx: "spacing-2" }}>
                        {formatTimestampDate(invoice.dueDate)}
                      </Text>
                    </Text>
                  )}
                </GridColumn>
                <GridColumn
                  size={2}
                  sx={{ alignItems: "end", color: "color-current-brand-800" }}
                >
                  <Text variant="h5">
                    {formatCurrency(invoice.subtotal + invoice.taxes)}
                  </Text>
                  {promptPayDiscount && (
                    <Text variant="body-1-regular">
                      {formatCurrency(invoice.total)}
                    </Text>
                  )}
                </GridColumn>
              </Grid>
            </GridColumn>
            <GridColumn size={4} push={[0, 4, 8]}>
              <Box sx={{ display: "inline-block", pt: "spacing-6" }}>
                <Divider color="color-current-brand-600" />
              </Box>
            </GridColumn>
          </Grid>

          {showPaymentDetails && (
            <Grid sx={{ pt: "spacing-6" }}>
              <GridColumn size={4} push={[0, 4, 8]}>
                <PaymentDetails
                  payment={paymentInfo}
                  paymentStatus={invoice.status}
                />
              </GridColumn>
            </Grid>
          )}
        </Box>
      </GridColumn>

      {/* TODO: hook up functionality for payment buttons. https://modernatx.atlassian.net/browse/MD-302 */}
      {showPayNow && (
        <GridColumn
          size={[4, 4, 3]}
          push={[0, 4, 9]}
          sx={{ pt: "spacing-9", pb: "spacing-2" }}
        >
          <Button
            icon="arrow-right"
            iconPosition="end"
            onClick={() => {
              history.push(`/pay?invoice=${invoice.invoiceNo}`);
            }}
          >
            Pay now
          </Button>
        </GridColumn>
      )}
      {/* TODO: hook up functionality for payment buttons. https://modernatx.atlassian.net/browse/MD-302 */}
      {showSchedulePayment && (
        <GridColumn size={[4, 4, 3]} push={[0, 4, 9]}>
          <Button
            variant="subtle"
            onClick={() => {
              setScheduleModalType(SchedulePayTypes.IndividualInvoices);
              setShowScheduleModal(true);
            }}
          >
            Schedule a payment
          </Button>
        </GridColumn>
      )}
      {/* TODO: hook up functionality for payment buttons. https://modernatx.atlassian.net/browse/MD-302 */}
      {showManagePayment && (
        <GridColumn size={[4, 4, 3]} push={[0, 4, 9]} sx={{ mt: "spacing-3" }}>
          <Box sx={{ display: "flex", gap: "spacing-2" }}>
            <Icon icon="arrow-counter-clockwise" size="xs" />
            <Text>
              <Text sx={{ pr: "spacing-2" }}>
                You have a payment scheduled for
              </Text>
              {formatDate(invoice.payment?.paymentScheduledDate)}.
              <Link
                sx={{ pl: "spacing-2" }}
                onClick={() => {
                  setScheduleModalType(
                    SchedulePayTypes.IndividualInvoicesUpdate
                  );
                  setShowScheduleModal(true);
                }}
              >
                Manage
              </Link>
            </Text>
          </Box>
        </GridColumn>
      )}
    </Grid>
  );
};

const InvoiceHeader: React.FC<{
  showManagePayment: boolean;
  paymentScheduledDate: string | undefined;
  onClickDownload: () => void;
}> = ({ showManagePayment = false, paymentScheduledDate, onClickDownload }) => {
  return (
    <Grid sx={{ pt: "spacing-2", pb: "spacing-6" }}>
      <GridColumn size={[2, 4, 6]} sx={{ alignItems: "start" }}>
        <Text variant="h4">Invoice Details</Text>
      </GridColumn>
      <GridColumn size={[2, 4, 6]} sx={{ alignItems: "end" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {/* TODO: add Share functionality once available. https://modernatx.atlassian.net/browse/MD-277 */}
          <Button
            variant="outline"
            icon="download-simple"
            iconPosition="end"
            onClick={onClickDownload}
          >
            Download invoice
          </Button>
          {/* <Button
            variant="outline"
            icon="share"
            iconPosition="end"
            onClick={() => {}}
            sx={{ ml: "spacing-3" }}
          >
            Share
          </Button> */}
        </Box>
      </GridColumn>
      {showManagePayment && (
        <GridColumn sx={{ mt: "spacing-6" }}>
          <Banner
            variant="info"
            text={
              <Text variant="body-1-bold">
                <Text sx={{ pr: "spacing-2" }}>
                  A one-time payment is scheduled on
                </Text>
                {formatDate(paymentScheduledDate)}.
                {/* TODO: hook up functionality for payment buttons. https://modernatx.atlassian.net/browse/MD-302 */}
                <Link
                  sx={{ pl: "spacing-2", fontWeight: "font-weight-regular" }}
                >
                  Manage
                </Link>
              </Text>
            }
          />
        </GridColumn>
      )}
    </Grid>
  );
};

const NoteCard: React.FC<{ notes: string[] }> = ({ notes }) => (
  <Box
    sx={{
      borderRadius: "radius-2",
      borderColor: "stroke-neutral-tertiary",
      backgroundColor: "color-system-yellow-50",
      overflow: "hidden",
      boxShadow: "shadow-200"
    }}
  >
    <Text
      weight="bold"
      as="div"
      sx={{
        backgroundColor: "color-system-yellow-200",
        gap: "spacing-4",
        px: "spacing-6",
        py: "spacing-4"
      }}
    >
      Note
    </Text>
    {notes.map((note, index) => (
      <Text
        key={index}
        as="p"
        sx={{
          gap: "spacing-4",
          px: "spacing-6",
          py: "spacing-4"
        }}
      >
        {note}
      </Text>
    ))}
  </Box>
);

const ItemizedInvoiceTable: React.FC<{ items: InvoiceItem[] }> = ({
  items
}) => (
  <Table variant="unshaded">
    <TableHeaderRow>
      <TableRow>
        <TableHeaderCell>
          <Text weight="bold">NDC</Text>
        </TableHeaderCell>
        <TableHeaderCell grow={4}>
          <Text weight="bold">Items</Text>
        </TableHeaderCell>
        <TableHeaderCell>
          <Text weight="bold">Contract price</Text>
        </TableHeaderCell>
        <TableHeaderCell>
          <Text weight="bold">Doses</Text>
        </TableHeaderCell>
        <TableHeaderCell>
          <Text weight="bold">Subtotal</Text>
        </TableHeaderCell>
      </TableRow>
    </TableHeaderRow>
    <TableBody>
      {items.map((item, index) => (
        <TableRow key={index}>
          <TableCell>{item.ndcNumber}</TableCell>
          <TableCell>{item.itemDescription}</TableCell>
          <TableCell>{formatCurrency(item.contractPrice)}</TableCell>
          <TableCell>{item.quantity}</TableCell>
          <TableCell>{formatCurrency(item.subtotal)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default InvoiceDetailPage;
